import { useEffect, useState, useCallback, useMemo } from "react";
import api from "../services/api";
import ReviewerSidebar from "../components/ReviewerSidebar";
import toast from "react-hot-toast";

export default function ReviewerDashboard() {
  const [esmps, setEsmps] = useState([]);
  const [selectedESMP, setSelectedESMP] = useState(null);
  const [status, setStatus] = useState("");
  const [comments, setComments] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Better: parse user once and memoize
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const fetchESMPs = useCallback(async () => {
    if (!user?.id) {
      toast.error("User not found. Please log in again.");
      setFetching(false);
      return;
    }

    setFetching(true);

    try {
      const res = await api.get("/esmp/my-esmps");

      const esmpArray = Array.isArray(res.data?.data) ? res.data.data : [];
      setEsmps(esmpArray);
    } catch (err) {
      console.error("Failed to fetch assigned ESMPs:", err);
      toast.error(
        err.response?.data?.message || "Failed to load assigned ESMPs",
      );
      setEsmps([]);
    } finally {
      setFetching(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchESMPs();
  }, [fetchESMPs]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (!status) {
      toast.error("Please select a review decision");
      return;
    }

    const trimmedComments = comments.trim();
    if (status === "Rejected" && !trimmedComments) {
      toast.error("Comments are required when rejecting an ESMP");
      return;
    }

    if (!selectedESMP?.id) return;

    const toastId = toast.loading("Submitting review...");

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("status", status);
      formData.append("review_comments", trimmedComments);
      if (file) formData.append("file", file);

      await api.put(`/esmp/review/${selectedESMP.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Review submitted successfully", { id: toastId });

      // Reset
      setSelectedESMP(null);
      setStatus("");
      setComments("");
      setFile(null);

      // Refresh list
      await fetchESMPs();
    } catch (err) {
      console.error("Review submission failed:", err);
      toast.error(err.response?.data?.message || "Failed to submit review", {
        id: toastId,
      });
    } finally {
      setLoading(false);
    }
  };

  // Derived lists (memoized for better performance)
  const pendingESMPs = useMemo(
    () =>
      esmps.filter(
        (e) => !["Approved", "Returned", "Rejected"].includes(e.status),
      ),
    [esmps],
  );

  const reviewedESMPs = useMemo(
    () =>
      esmps.filter((e) =>
        ["Approved", "Returned", "Rejected"].includes(e.status),
      ),
    [esmps],
  );

  if (fetching) {
    return (
      <div className="flex min-h-screen bg-gray-100 items-center justify-center">
        <div className="text-green-700 text-lg">
          Loading your assigned reviews...
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ReviewerSidebar />

      <main className="flex-1 p-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold">Reviewer Dashboard</h2>
          <p className="text-sm mt-1">
            Welcome, {user.name || user.email || "Reviewer"}
          </p>
        </div>

        {/* Pending Reviews */}
        <section className="bg-white p-6 rounded-xl shadow">
          <h3 className="text-lg font-semibold text-green-700 mb-4">
            Pending Reviews ({pendingESMPs.length})
          </h3>

          {pendingESMPs.length === 0 ? (
            <p className="text-gray-500 py-4">
              No pending reviews assigned to you at the moment.
            </p>
          ) : (
            <div className="space-y-3">
              {pendingESMPs.map((esmp) => (
                <div
                  key={esmp.id}
                  className="border rounded-lg p-4 cursor-pointer hover:bg-green-50 transition"
                  onClick={() => setSelectedESMP(esmp)}
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">
                        {esmp.project_name}
                      </p>
                      <p className="text-sm text-gray-600">
                        District: {esmp.district}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        ESMP ID: {esmp.esmp_id}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        esmp.status === "Submitted"
                          ? "bg-yellow-100 text-yellow-800"
                          : esmp.status === "Overdue"
                            ? "bg-red-100 text-red-800"
                            : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {esmp.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Review Modal */}
        {selectedESMP && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <form
              onSubmit={handleSubmitReview}
              className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 space-y-5">
                <h4 className="text-xl font-bold text-green-700">
                  Review ESMP
                </h4>

                <div className="text-sm space-y-1.5 bg-gray-50 p-4 rounded-lg">
                  <p>
                    <strong>Project:</strong> {selectedESMP.project_name}
                  </p>
                  <p>
                    <strong>District:</strong> {selectedESMP.district}
                  </p>
                  <p>
                    <strong>ESMP ID:</strong> {selectedESMP.esmp_id}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Decision <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    required
                  >
                    <option value="">-- Select decision --</option>
                    <option value="Approved">Approve</option>
                    <option value="Returned">Return for Revision</option>
                    <option value="Rejected">Reject</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Comments
                    {status === "Rejected" && (
                      <span className="text-red-500"> *</span>
                    )}
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[120px]"
                    placeholder="Enter detailed review comments..."
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    required={status === "Rejected"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Attach Annotated Document (optional)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold text-white transition ${
                      loading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-700"
                    }`}
                  >
                    {loading ? "Submitting..." : "Submit Review"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedESMP(null);
                      setStatus("");
                      setComments("");
                      setFile(null);
                    }}
                    className="flex-1 py-3 px-4 rounded-lg border border-gray-300 font-medium hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Reviewed ESMPs */}
        <section className="bg-white p-6 rounded-xl shadow">
          <h3 className="text-lg font-semibold text-green-700 mb-4">
            My Reviewed ESMPs ({reviewedESMPs.length})
          </h3>

          {reviewedESMPs.length === 0 ? (
            <p className="text-gray-500 py-4">
              You haven't reviewed any ESMPs yet.
            </p>
          ) : (
            <div className="space-y-3">
              {reviewedESMPs.map((esmp) => (
                <div key={esmp.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">
                        {esmp.project_name}
                      </p>
                      <p className="text-sm text-gray-600">
                        District: {esmp.district}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        esmp.status === "Approved"
                          ? "bg-green-100 text-green-800"
                          : esmp.status === "Returned"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                      }`}
                    >
                      {esmp.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
